// AdminAppClaimsPage — Phase 3 of unified back-office.
// Moderates producer claims on app.geovinum.io.
import { useCallback, useEffect, useState } from "react";
import {
  Shield, Check, X, RefreshCw, Loader2, AlertCircle, ExternalLink, Building2,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const AdminAppClaimsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState({}); // { claim_id: string }
  const [pending, setPending] = useState({}); // { claim_id: bool }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listAppClaims();
      const list = Array.isArray(res) ? res : res?.items || res?.claims || [];
      setItems(list);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const decide = async (claimId, decision) => {
    setPending((p) => ({ ...p, [claimId]: true }));
    try {
      await adminApi.decideAppClaim(claimId, decision, notes[claimId] || "");
      setItems((prev) => prev.filter((c) => (c.id || c._id) !== claimId));
      setNotes((n) => { const { [claimId]: _, ...rest } = n; return rest; });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setPending((p) => ({ ...p, [claimId]: false }));
    }
  };

  return (
    <div data-testid="admin-app-claims">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Revendications producteurs</h1>
        <button
          onClick={fetchAll}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] hover:bg-[color:var(--gv-mist)] transition-colors"
          data-testid="app-claims-refresh-btn"
        >
          <RefreshCw size={13} /> Rafraîchir
        </button>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-6">Modération des demandes d'appropriation de fiches producteur — app.geovinum.io.</p>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="app-claims-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium mb-0.5">Impossible de récupérer les revendications</div>
            <div className="text-[12px] opacity-70 mt-1">Vérifiez que l'app.geovinum.io backend est en ligne.</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-claims-loading">
          <Loader2 size={14} className="animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-claims-empty">
          <Shield size={14} /> Aucune revendication en attente 🎉
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => {
            const cid = c.id || c._id || c.claim_id;
            return (
              <div key={cid} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid={`app-claim-card-${cid}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Building2 size={14} className="text-[color:var(--gv-pink)]" />
                      <span className="font-medium text-[color:var(--gv-ink)]">{c.producer_name || c.producer?.name || "—"}</span>
                      {c.producer_slug && (
                        <a href={`https://app.geovinum.io/producer/${c.producer_slug}`} target="_blank" rel="noreferrer" className="text-[11.5px] text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-pink)] inline-flex items-center gap-0.5">
                          {c.producer_slug} <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-[13px] text-[color:var(--gv-ink)]/75 mb-3">
                      <div><span className="text-[color:var(--gv-ink)]/50">Demandeur :</span> {c.claimant_name || c.email || "—"}</div>
                      <div><span className="text-[color:var(--gv-ink)]/50">Email :</span> {c.email || "—"}</div>
                      {c.phone && <div><span className="text-[color:var(--gv-ink)]/50">Téléphone :</span> {c.phone}</div>}
                      {c.website && <div><span className="text-[color:var(--gv-ink)]/50">Site :</span> <a href={c.website} target="_blank" rel="noreferrer" className="hover:text-[color:var(--gv-pink)]">{c.website}</a></div>}
                    </div>
                    {c.message && (
                      <div className="text-[13px] text-[color:var(--gv-ink)]/85 bg-[color:var(--gv-mist)] rounded-[4px] p-3 mb-3">{c.message}</div>
                    )}
                    <input
                      type="text"
                      placeholder="Note interne (optionnelle) — visible dans l'historique."
                      value={notes[cid] || ""}
                      onChange={(e) => setNotes({ ...notes, [cid]: e.target.value })}
                      className="gv-input w-full text-[12.5px]"
                      data-testid={`app-claim-note-${cid}`}
                    />
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => decide(cid, "approved")}
                      disabled={pending[cid]}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] bg-emerald-600 text-white text-[13px] hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`app-claim-approve-${cid}`}
                    >
                      {pending[cid] ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approuver
                    </button>
                    <button
                      onClick={() => decide(cid, "rejected")}
                      disabled={pending[cid]}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-red-300 text-red-700 text-[13px] hover:bg-red-50 disabled:opacity-50"
                      data-testid={`app-claim-reject-${cid}`}
                    >
                      <X size={13} /> Rejeter
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 mt-3">{items.length} revendication{items.length > 1 ? "s" : ""} en attente</div>
    </div>
  );
};

export default AdminAppClaimsPage;
